/**
 * Состояния данных: загрузка, пусто, ошибка.
 *
 * §0.4 требует, чтобы у каждого действия и списка были все три. Держим их
 * рядом: когда они в одном файле, забыть одно из них труднее.
 */

import { AlertTriangle, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "./Button";
import { cn } from "./cn";

/** Скелет вместо спиннера там, где известна форма содержимого. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-line/60", className)}
      aria-hidden
    />
  );
}

export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-2", className)} role="status" aria-label="Загрузка">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  /** Разные причины пустоты требуют разного текста: «ничего нет»
   *  и «ничего не найдено по фильтру» — это не одно и то же (§15.4). */
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
}

export function EmptyState({ title, description, icon: Icon, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      {Icon && <Icon className="size-8 text-faint" aria-hidden />}
      <p className="text-sm font-bold text-fg">{title}</p>
      {description && <p className="max-w-sm text-[13px] text-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

interface ErrorStateProps {
  title?: string;
  message: string;
  /** Идентификатор для обращения в поддержку — §21.2 требует его в каждой ошибке. */
  correlationId?: string;
  onRetry?: () => void;
  retrying?: boolean;
}

export function ErrorState({
  title = "Не удалось загрузить данные",
  message,
  correlationId,
  onRetry,
  retrying = false,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center"
    >
      <AlertTriangle className="size-8 text-danger" aria-hidden />
      <p className="text-sm font-bold text-fg">{title}</p>
      <p className="max-w-sm text-[13px] text-muted">{message}</p>
      {correlationId && (
        <p className="font-mono text-[11px] text-faint">Код обращения: {correlationId}</p>
      )}
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} loading={retrying} className="mt-2">
          Повторить
        </Button>
      )}
    </div>
  );
}
